import { useState } from "react";
import { toast } from "sonner";
import { Mic2, MessagesSquare, Radio, CalendarHeart } from "lucide-react";
import { MaskedLine, FadeUp, ParallaxImage, Page, Meta } from "../components/Reveal";
import { Field, SubmitButton, inputCls } from "../components/forms";
import { postConference } from "../lib/api";
import { useContent } from "../lib/ContentContext";

const FORMAT_ICONS = [Mic2, MessagesSquare, Radio, CalendarHeart];

const EVENT_TYPES = ["Grand public", "Milieu scolaire", "Milieu de soin", "Entreprise / organisation", "Autre"];

const initialForm = { name: "", email: "", organization: "", event_type: EVENT_TYPES[0], preferred_date: "", message: "" };

export default function Conferences() {
  const c = useContent();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const FORMATS = FORMAT_ICONS.map((Icon, i) => ({
    Icon,
    title: c(`conferences.format.${i + 1}.titre`),
    text: c(`conferences.format.${i + 1}.texte`),
  }));

  const TERRITOIRES = [1, 2, 3, 4].map((n) => ({
    n: String(n).padStart(2, "0"),
    title: c(`conferences.territoire.${n}.titre`),
    text: c(`conferences.territoire.${n}.texte`),
  }));

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postConference(form);
      toast.success("Votre invitation a bien été envoyée. Eugénie vous répondra rapidement.");
      setForm(initialForm);
    } catch {
      toast.error("Une erreur est survenue. Vérifiez les champs et réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page testId="conferences-page">
      <section className="mx-auto grid max-w-7xl items-center gap-16 px-6 pt-36 md:px-12 md:pt-48 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <FadeUp><Meta>{c("conferences.hero.meta")}</Meta></FadeUp>
          <h1 className="mt-6 font-serif text-5xl font-light leading-[1.08] tracking-tight md:text-7xl">
            <MaskedLine delay={0.2}>{c("conferences.hero.l1")}</MaskedLine>
            <MaskedLine delay={0.35} className="italic text-clay">{c("conferences.hero.l2")}</MaskedLine>
          </h1>
          <FadeUp delay={0.6} className="mt-10 max-w-xl">
            <p className="text-base leading-relaxed text-smoke md:text-lg">
              {c("conferences.hero.p")}
            </p>
          </FadeUp>
        </div>
        <div className="lg:col-span-5">
          <FadeUp delay={0.4} y={40}>
            <div className="relative ml-auto max-w-md">
              <div className="absolute -bottom-6 -left-6 h-full w-full rounded-[1.75rem] bg-sand" aria-hidden="true" />
              <ParallaxImage src={c("conferences.hero.img")} alt="Eugénie Larrivée sur scène"
                className="relative aspect-[4/5] rounded-[1.75rem] shadow-[0_30px_80px_-35px_rgba(42,40,37,0.4)]" speed={7} position="50% 12%" />
              <p className="mt-4 text-xs italic text-ash">{c("conferences.hero.note")}</p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section data-testid="formats-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <FadeUp><Meta>{c("conferences.formats.meta")}</Meta></FadeUp>
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          {FORMATS.map((f, i) => (
            <FadeUp key={f.title} delay={0.07 * i}>
              <div data-testid={`format-${f.title.toLowerCase()}`} className="group border-t border-linen pt-7">
                <f.Icon className="h-7 w-7 text-clay transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.25} />
                <h3 className="mt-5 font-serif text-xl font-light tracking-tight md:text-2xl">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-smoke">{f.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="territoires-section" className="bg-sage/40 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <FadeUp><Meta>{c("conferences.territoires.meta")}</Meta></FadeUp>
          <div className="mt-12">
            {TERRITOIRES.map((t, i) => (
              <FadeUp key={t.n} delay={0.07 * i}>
                <div data-testid={`territoire-${t.n}`}
                  className="grid gap-3 border-b border-charcoal/10 py-9 transition-colors duration-300 hover:bg-cream/50 md:grid-cols-12 md:gap-8 md:px-6">
                  <p className="font-serif text-3xl font-light text-clay/50 md:col-span-2">{t.n}</p>
                  <h3 className="font-serif text-2xl font-light leading-snug tracking-tight md:col-span-5 md:text-3xl">{t.title}</h3>
                  <p className="text-sm leading-relaxed text-smoke md:col-span-5 md:text-base">{t.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section id="demande" data-testid="conference-form-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp><Meta>{c("conferences.form.meta")}</Meta></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] tracking-tight md:text-5xl">
                {c("conferences.form.t1")} <em className="text-clay">{c("conferences.form.t2")}</em>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-smoke">
                {c("conferences.form.p")}
              </p>
              <p className="mt-10 font-hand text-3xl text-clay">{c("conferences.form.hand")}</p>
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <form data-testid="conference-request-form" onSubmit={onSubmit} className="space-y-8">
              <div className="grid gap-8 md:grid-cols-2">
                <Field label="Nom complet *" htmlFor="conf-name">
                  <input id="conf-name" data-testid="conference-name-input" required minLength={2}
                    value={form.name} onChange={set("name")} placeholder="Votre nom" className={inputCls} />
                </Field>
                <Field label="Courriel *" htmlFor="conf-email">
                  <input id="conf-email" data-testid="conference-email-input" required type="email"
                    value={form.email} onChange={set("email")} placeholder="vous@exemple.com" className={inputCls} />
                </Field>
              </div>
              <div className="grid gap-8 md:grid-cols-2">
                <Field label="Organisation *" htmlFor="conf-org">
                  <input id="conf-org" data-testid="conference-organization-input" required minLength={2}
                    value={form.organization} onChange={set("organization")} placeholder="École, entreprise, organisme…" className={inputCls} />
                </Field>
                <Field label="Format souhaité *" htmlFor="conf-type">
                  <select id="conf-type" data-testid="conference-event-type-select"
                    value={form.event_type} onChange={set("event_type")} className={`${inputCls} cursor-pointer`}>
                    {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Date souhaitée (optionnel)" htmlFor="conf-date">
                <input id="conf-date" data-testid="conference-date-input" type="date"
                  value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
              </Field>
              <Field label="Votre projet *" htmlFor="conf-message">
                <textarea id="conf-message" data-testid="conference-message-input" required minLength={10} rows={4}
                  value={form.message} onChange={set("message")}
                  placeholder="Le public, le contexte, vos besoins…" className={`${inputCls} resize-none`} />
              </Field>
              <SubmitButton loading={loading} testId="conference-submit-button">
                {c("conferences.form.bouton")}
              </SubmitButton>
            </form>
          </FadeUp>
        </div>
      </section>
    </Page>
  );
}
